/**
 * @fileoverview The upgrade popup's body — plan tiles, billing cycle, and the
 * embedded Stripe Checkout form.
 *
 * Rendered inside the Settings subscription dialog, which owns the chrome (the
 * title and the close control), so nothing here draws a header of its own: two
 * close buttons stacked in the same corner is what a second header produces.
 *
 * Shape of the choice, in the order a buyer makes it:
 *
 *   1. **One cycle toggle at the top**, defaulting to MONTHLY. It governs every
 *      tile at once — the alternative, a cycle choice per tile, asks the same
 *      question twice and lets the two answers disagree on screen. Monthly is
 *      the default because it is the smaller commitment; annual has to earn the
 *      switch, which is what the free-months line beneath the toggle is for.
 *   2. **A tile per plan that can actually be bought**, priced for whichever
 *      cycle is selected, so flipping the toggle re-prices the whole ladder in
 *      place rather than sending anyone back a step.
 *   3. **One button to payment.**
 *
 * Note "Studio AI" is the display name; the internal tier key stays
 * `concertmaster` (price ids, PRICE_TIER_MAP) — do not rename the id.
 */

import { useState, useCallback, useEffect } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import CheckoutUnavailable from "@/components/CheckoutUnavailable";
import SubscriptionComingSoon from "@/components/SubscriptionComingSoon";
import { getStripe, stripeConfigured } from "@/lib/stripeClient";
import { SUBSCRIPTION_PRICING_VISIBLE } from "@/lib/featureFlags";
import { SegmentedControl } from "@/components/studio/primitives";
import {
  ANNUAL_FRAMING,
  annualPriceFromMonthly,
  formatPriceFromCents,
  monthsFreeFromPrices,
  monthsFreeLabel,
} from "@/lib/pricing";
import {
  OFFERED_TIERS,
  TIER_ALLOWANCE_LABEL,
  TIER_FEATURES,
  TIER_NOTE,
  TIER_TAGLINE,
  tierDisplayName,
  tierMonthlyCents,
} from "@/lib/tiers";

/**
 * What each tile says the plan includes: the SAME bullets the public pricing
 * page shows, from the shared catalogue, so the two surfaces cannot describe
 * one plan two ways (the tiles used to carry three bullets of their own
 * against the pricing page's six, and quietly omitted things it had promised).
 *
 * An ABSENCE is marked for any tier the catalogue carries a note for —
 * `studio`, deliberately: it is the first paid plan in Hans's history with no
 * AI, a list of ticks can only say what is included, and someone upgrading
 * from a free plan that DOES record has every reason to assume a paid one
 * records more. The tile states the absence in the catalogue's short form;
 * the pricing page carries the full sentence.
 */
const tilePoints = (id) => [
  ...(TIER_FEATURES[id] || []).map((text) => ({ text, included: true })),
  ...(TIER_NOTE[id] ? [{ text: TIER_ALLOWANCE_LABEL[id], included: false }] : []),
];

// X9: the tiles name the tiers that are actually on sale and send the wire
// aliases that resolve to their prices. Both halves had to move together —
// repointing the aliases while the tiles still read "Pro" and "Studio" would
// have charged the right amount under the wrong plan name, which is worse than
// the 400 the old aliases earned ("That plan is no longer available").
//
// Names, prices and taglines come from `lib/tiers`; only the wire aliases live
// here, because they are checkout's business and nothing else's. The alias set
// is the server's (`stripe_service._PLAN_ALIAS_ENV`) — the four legacy ones are
// still accepted there so an older shipped build gets a clean refusal naming
// the plan rather than an opaque failure.
//
// I10: a tier declares ONE figure, its monthly price. The annual price and the
// savings badge are derived from it by the ten-for-twelve rule, so they cannot
// disagree with each other or with the convention — which they did, as
// "$199 / Save 15%" beside $19 a month and "$519 / Save 12%" beside $49.
const TIERS = OFFERED_TIERS.map((id) => ({
  id,
  name: tierDisplayName(id),
  monthlyCents: tierMonthlyCents(id),
  description: TIER_TAGLINE[id],
  points: tilePoints(id),
  monthlyPriceId: `${id}_monthly`,
  annualPriceId: `${id}_annual`,
}));

// The tile selected on open — the top tier, which is what most instructors are
// here for. Derived so it cannot name a tile that no longer exists.
const DEFAULT_TIER = TIERS[TIERS.length - 1]?.id;

const CYCLES = [
  { id: "monthly", label: "Monthly", testId: "cycle-monthly" },
  { id: "annual", label: "Annual", testId: "cycle-annual" },
];

/**
 * A selectable plan tile. Price and period follow the cycle toggle above it.
 */
const TierTile = ({ tier, selected, cycle, onSelect }) => {
  const annualCents = annualPriceFromMonthly(tier.monthlyCents);
  const isAnnual = cycle === "annual";
  const price = formatPriceFromCents(isAnnual ? annualCents : tier.monthlyCents);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => onSelect(tier.id)}
      data-testid={`tier-tile-${tier.id}`}
      className={`flex flex-col rounded-2xl border p-5 text-left transition-colors md:p-6 ${
        selected
          ? "border-[var(--accent-yellow)]/40 bg-[var(--accent-yellow)]/5"
          : "border-[var(--studio-divider)] bg-[var(--studio-interactive)]/40 hover:border-[var(--studio-divider-strong)]"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-serif text-lg font-medium text-[var(--text-primary)]">{tier.name}</h4>
        <span
          aria-hidden="true"
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
            selected
              ? "border-[var(--accent-yellow)] bg-[var(--accent-yellow)]"
              : "border-[var(--studio-divider-strong)]"
          }`}
        >
          {selected && <Check className="h-2.5 w-2.5 text-[var(--text-inverse)]" strokeWidth={3} />}
        </span>
      </div>

      <p className="mt-3 flex items-baseline gap-1.5">
        <span className="font-mono text-[28px] leading-none text-[var(--text-primary)]">{price}</span>
        {/* --text-secondary, not tertiary: at 13px the caption token is
            3.52:1 on the raised card, under the 4.5:1 AA floor, and this
            is the half of the price that says what you are agreeing to
            pay per. Caught by scripts/theme-audit.mjs. */}
        <span className="text-[13px] text-[var(--text-secondary)]">{isAnnual ? "/year" : "/month"}</span>
      </p>
      <p className="mt-2 text-[13px] text-[var(--text-secondary)]">{tier.description}</p>

      <ul className="mt-4 space-y-1.5 text-[13px] text-[var(--text-secondary)]">
        {tier.points.map((point) => (
          <li key={point.text} className="flex items-start gap-2">
            <span
              aria-hidden="true"
              className={`mt-px w-3 shrink-0 text-center ${point.included ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-quaternary)]"}`}
            >
              {point.included ? "✓" : "—"}
            </span>
            {/* The ABSENCE line ("No AI recording") was dimmed to tertiary
                to read as excluded, which put the one material thing a paid
                plan does NOT include at 3.52:1. The exclusion is already
                carried by the marker beside it, so the text does not have
                to be hard to read to say "not included". */}
            <span>{point.text}</span>
          </li>
        ))}
      </ul>
    </button>
  );
};

/**
 * Plan selection → embedded Stripe Checkout.
 *
 * @param {object} props
 * @param {function} [props.onStepChange] - Called with "select" | "checkout" |
 *   "error" whenever the step changes. The two dialogs that host this need it
 *   to size themselves: the plan grid wants 960px so the tiles sit side by side
 *   with unwrapped bullets, and Stripe's form is a ~400px centred column that
 *   is marooned in the middle of that — two thirds of the popup empty white.
 *   Reported rather than lifted, because the step is genuinely this
 *   component's business and a host that ignores it still works.
 * @returns {React.ReactElement}
 */
const StripeCheckout = ({ onStepChange }) => {
  // Defaults to the AI tier: Hans is an AI lesson assistant, and Studio exists
  // for teachers who deliberately want the platform without it.
  const [selectedTier, setSelectedTier] = useState(DEFAULT_TIER);
  // Monthly by design — the smaller commitment is the safe default, and the
  // annual deal is stated rather than pre-selected on someone's behalf.
  const [selectedCycle, setSelectedCycle] = useState("monthly");
  const [step, setStep] = useState("select"); // "select" | "checkout" | "error"
  const [error, setError] = useState(null);

  // On the step itself rather than at each setStep, so the error path — which
  // is set from inside fetchClientSecret, not from a click — reports too.
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Falls back to the first offered tier rather than undefined — this component
  // reads `tier.annualPriceId` unconditionally, so a selection that does not
  // match a tile would take the whole Settings page down with it.
  const tier = TIERS.find((t) => t.id === selectedTier) || TIERS[0];
  const priceId = selectedCycle === "annual" ? tier.annualPriceId : tier.monthlyPriceId;

  // The badge is read back off the two figures that are actually on screen for
  // the selected plan rather than from the rule, so it can never describe a
  // deal the tiles do not show.
  const annualBadge = monthsFreeLabel(
    monthsFreeFromPrices(tier.monthlyCents, annualPriceFromMonthly(tier.monthlyCents)),
  );

  const fetchClientSecret = useCallback(async () => {
    try {
      const res = await api.post("/subscription/stripe/create-checkout-session", {
        price_id: priceId,
        return_url: `${window.location.origin}/checkout/return`,
      });
      return res.data.client_secret;
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to start checkout.";
      setError(msg);
      setStep("error");
      throw err;
    }
  }, [priceId]);

  if (step === "error") {
    return (
      <div>
        <p className="text-sm text-[var(--state-danger)]">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setStep("select");
          }}
          className="mt-4 text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
        >
          Try again
        </button>
      </div>
    );
  }

  // The kill switch for the paywall itself. This is the surface where money
  // actually changes hands, so it is the one place a stale price is not merely
  // misleading but chargeable — which is why the branch is kept even now that
  // the prices are live: taking the ladder back down must stay a one-line
  // change, with the checkout machinery below left intact and simply
  // unreachable.
  if (!SUBSCRIPTION_PRICING_VISIBLE) {
    return <SubscriptionComingSoon note="Your current plan and usage are unchanged." />;
  }

  if (step === "select") {
    return (
      <div>
        {/* One toggle, governing every tile. */}
        <div className="flex justify-center">
          <SegmentedControl
            options={CYCLES}
            value={selectedCycle}
            onChange={setSelectedCycle}
            testId="billing-cycle-toggle"
          />
        </div>

        {/* The convention only sells itself once the free months are placed in
            the year: teaching runs September to June, so the two months nobody
            pays for are the two months nobody teaches. Shown against the annual
            option, which is the one it describes. */}
        <div className="mt-3 mb-5 h-9 text-center">
          {selectedCycle === "annual" && annualBadge && (
            <p className="text-xs text-[var(--text-tertiary)]" data-testid="annual-framing">
              <span className="mr-2 rounded-full bg-[var(--accent-yellow)]/20 px-2 py-0.5 text-[10px] font-semibold text-[var(--accent-yellow-text)]">
                {annualBadge}
              </span>
              {ANNUAL_FRAMING}
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2 md:gap-5" role="radiogroup" aria-label="Choose a plan">
          {TIERS.map((t) => (
            <TierTile
              key={t.id}
              tier={t}
              cycle={selectedCycle}
              selected={selectedTier === t.id}
              onSelect={setSelectedTier}
            />
          ))}
        </div>

        <button
          onClick={() => setStep("checkout")}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-3 text-sm font-medium text-[var(--text-inverse)] transition-transform active:scale-95"
        >
          Continue to Payment <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  // step === "checkout"
  return (
    <div>
      <button
        onClick={() => setStep("select")}
        className="mb-4 flex items-center gap-1 text-sm text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to plans
      </button>
      {/* A build with no publishable key hands the provider a null `stripe`,
          which it parses as "empty" and then never initialises — an empty div
          under "Back to plans", with no error and no request. That is what the
          production upgrade dialog was for the whole of the pricing launch, so
          the state is stated rather than rendered. Checked here, not at the
          top: the plan tiles are worth showing either way, and this is the
          only step that needs Stripe. */}
      {stripeConfigured() ? (
        <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      ) : (
        <CheckoutUnavailable note="Your current plan is unchanged." />
      )}
    </div>
  );
};

export default StripeCheckout;
